import type { ProgrammaticLandingPageConfig } from '../types'
import { KWP_SIZE_FACTS } from './kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[10]

export const KWP_10_PHOTOVOLTAIK_KOSTEN: ProgrammaticLandingPageConfig = {
  slug: '10-kwp-photovoltaik-kosten',
  lang: 'de',
  topicId: 'kwp-kosten-10',
  title: '10 kWp Photovoltaikanlage: Kosten & Richtwerte 2026',
  metaDescription:
    'Was kostet eine 10-kWp-Photovoltaikanlage? Dachfläche, Modulanzahl, Kostenaufschlüsselung und ' +
    'kostenloser Preisvergleich für die beliebteste Familien-Anlagengröße.',
  keywords: [
    '10 kWp Photovoltaik Kosten',
    '10 kWp Solaranlage Preis',
    'Photovoltaikanlage 10 kWp',
    'Was kostet eine 10 kWp Anlage',
    '10 kWp PV-Anlage Haushalt',
  ],
  eyebrow: 'Anlagengröße 10 kWp',
  h1: '10 kWp Photovoltaikanlage: Kosten und Richtwerte',
  intro:
    '10 kWp ist eine der meistgewählten Anlagengrößen für Einfamilienhäuser - unter anderem, weil ' +
    'bis zu dieser Grenze eine eigene EEG-Teileinspeisevergütung gilt. Hier finden Sie Richtwerte ' +
    'zu Dachfläche und Modulanzahl, eine Kostenaufschlüsselung und direkt darunter einen Rechner ' +
    'mit dem für 10 kWp vorbelegten Durchschnittspreis aus echten Handwerker-Angeboten.',
  calculatorPrefill: { kind: 'kwp', kwp: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Für wen eignet sich eine 10-kWp-Anlage?' },
    {
      type: 'paragraph',
      text:
        'Richtwert: 10 kWp passen typischerweise zu einem 4-5-Personen-Haushalt bzw. einem ' +
        'klassischen Einfamilienhaus mit einem Jahresverbrauch von etwa 5.000-7.000 kWh - oft in ' +
        'Kombination mit einem Batteriespeicher, um den Eigenverbrauch zu erhöhen.',
    },
    { type: 'heading', text: 'Dachfläche und Module: das braucht eine 10-kWp-Anlage' },
    {
      type: 'paragraph',
      text:
        `Richtwert: rund ${FACTS.roofAreaM2} m² Dachfläche und ca. ${FACTS.moduleCount} Module bei ` +
        `marktüblichen ~440-Wp-Modulen. Der Jahresertrag liegt bei Südausrichtung ohne Verschattung ` +
        `bei etwa ${FACTS.annualYieldKwh.toLocaleString('de-DE')} kWh. Eine genaue, dachspezifische ` +
        'Berechnung liefert der kostenlose PV-Rechner.',
    },
    { type: 'heading', text: 'Kostenbestandteile einer Photovoltaikanlage' },
    {
      type: 'list',
      items: [
        'Module: größter Einzelposten, meist rund ein Drittel der Gesamtkosten',
        'Wechselrichter: bei 10 kWp je nach Hausanschluss ein- oder dreiphasig',
        'Montage/Arbeitsleistung: Befestigung auf dem Dach, Verkabelung',
        'Gerüst: abhängig von Dachhöhe und -zugänglichkeit',
        'Elektroinstallation: Zählerschrank-Anpassung, Anschluss ans Hausnetz',
        'Anmeldung: Netzbetreiber und Marktstammdatenregister',
      ],
    },
    {
      type: 'paragraph',
      text:
        '10 kWp ist zusätzlich eine rechtlich relevante Grenze: bis dahin gilt eine eigene, ' +
        'höhere EEG-Teileinspeisevergütung (Details im Förder-Überblick) - für die Kalkulation der ' +
        'Handwerkspreise selbst spielt das aber keine Rolle, hier zählt weiterhin der Preis pro kWp.',
    },
    { type: 'heading', text: 'Amortisation und Eigenverbrauch' },
    {
      type: 'paragraph',
      text:
        'Mit passend dimensioniertem Batteriespeicher lässt sich der Eigenverbrauchsanteil bei ' +
        'einer 10-kWp-Anlage deutlich über den Wert ohne Speicher (typischerweise rund 25-30 %) ' +
        'steigern. Bis 10 kWp gilt für die Teileinspeisung aktuell 7,78 ct/kWh (Stand Februar 2026, ' +
        'sinkt zum 1. August 2026 auf 7,71 ct/kWh) - Details dazu und zu weiteren Förderungen im ' +
        'Förder-Überblick, eine genaue Berechnung für Ihr Dach liefert der PV-Rechner.',
    },
  ],
  faq: [
    {
      q: 'Warum ist 10 kWp eine besonders beliebte Anlagengröße?',
      a: 'Zum einen passt sie zur typischen Größe eines Einfamilienhaus-Verbrauchs, zum anderen gilt bis 10 kWp eine eigene, höhere EEG-Teileinspeisevergütung - Details dazu im Förder-Überblick.',
    },
    {
      q: 'Ändert sich bei mehr als 10 kWp etwas bei der Einspeisevergütung?',
      a: 'Ja, oberhalb von 10 kWp gilt ein anderer Vergütungssatz für den eingespeisten Strom. Die genauen, aktuell gültigen Sätze finden Sie im Förder-Überblick - für den Preisvergleich der Handwerksleistung selbst ist das unabhängig von der Vergütung.',
    },
    {
      q: 'Reicht 10 kWp für ein Einfamilienhaus mit Speicher?',
      a: 'In den meisten Fällen ja - 10 kWp gilt als eine der Standardgrößen für Einfamilienhäuser mit Batteriespeicher. Bei zusätzlicher Wärmepumpe oder Elektroauto lohnt sich ein Blick auf 12 kWp oder mehr.',
    },
  ],
  primaryCta: { label: 'Anlagengröße für mein Dach kostenlos berechnen', action: 'link', href: '/pv-rechner' },
  secondaryCta: { label: 'Direkt Anfrage erstellen', action: 'customer' },
  relatedLinks: [
    { label: '8 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/8-kwp-photovoltaik-kosten' },
    { label: '12 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/12-kwp-photovoltaik-kosten' },
    { label: '15 kWp Photovoltaikanlage: Kosten & Richtwerte', href: '/15-kwp-photovoltaik-kosten' },
    { label: '10 kWh Batteriespeicher: Kosten & Richtwerte', href: '/10-kwh-speicher-kosten' },
    { label: 'Was kostet eine Solaranlage? Preise & Einflussfaktoren im Überblick', href: '/solaranlage-kosten' },
    { label: 'Photovoltaik-Förderung 2026: Deutschland & Österreich', href: '/blog/photovoltaik-foerderung-2026' },
    { label: 'PV-Kostenrechner: Durchschnittspreis für Ihre Anlage berechnen', href: '/pv-kostenrechner' },
  ],
}
